import React, { useState, useRef, useMemo } from 'react';
import toast from 'react-hot-toast';
import GameKeypad from './GameKeypad';

const VIP_PATTERNS = [
  { label: '1111', value: 1111 },
  { label: '2222', value: 2222 },
  { label: '3333', value: 3333 },
  { label: '4444', value: 4444 },
  { label: '5555', value: 5555 },
  { label: '6666', value: 6666 },
  { label: '7777', value: 7777 },
  { label: '8888', value: 8888 },
  { label: '9999', value: 9999 },
  { label: '1234', value: 1234 },
  { label: '0001', value: 1 },
];

function NumberPickerApex({ game, selectedNumbers, onSelect, onRemove, blockedPickNumbers, maxSelectableNumbers }) {
  const blocked = blockedPickNumbers ?? new Set(selectedNumbers);
  const maxPicks = maxSelectableNumbers ?? game.maxNumbersPerTicket;
  const [inputBuffer, setInputBuffer] = useState('');
  const clearTimeoutRef = useRef(null);
  const maxDigits = 4;

  const clearDisplay = () => {
    if (clearTimeoutRef.current) {
      clearTimeout(clearTimeoutRef.current);
      clearTimeoutRef.current = null;
    }
    setInputBuffer('');
  };

  const handleDigit = (digit) => {
    if (clearTimeoutRef.current) {
      clearTimeout(clearTimeoutRef.current);
      clearTimeoutRef.current = null;
    }

    if (inputBuffer.length < maxDigits) {
      const newBuffer = inputBuffer + digit;
      setInputBuffer(newBuffer);

      if (newBuffer.length === maxDigits) {
        const num = parseInt(newBuffer, 10);
        if (num >= 0 && num <= 9999) {
          if (blocked.has(num)) {
            toast.error(`Number ${String(num).padStart(4, '0')} already selected`);
            clearDisplay();
          } else if (selectedNumbers.length < maxPicks) {
            onSelect(num);
            clearTimeoutRef.current = setTimeout(() => {
              setInputBuffer('');
              clearTimeoutRef.current = null;
            }, 500);
          } else {
            toast.error(`Maximum ${maxPicks} numbers allowed this round`);
            clearDisplay();
          }
        } else {
          clearDisplay();
        }
      }
    }
  };

  const handleBackspace = () => {
    if (clearTimeoutRef.current) {
      clearTimeout(clearTimeoutRef.current);
      clearTimeoutRef.current = null;
    }
    setInputBuffer((prev) => prev.slice(0, -1));
  };

  const handleClear = () => {
    clearDisplay();
  };

  const addVipNumber = (num) => {
    if (blocked.has(num)) {
      toast.error(`Number ${String(num).padStart(4, '0')} already selected`);
      return;
    }
    if (selectedNumbers.length >= maxPicks) {
      toast.error(`Maximum ${maxPicks} numbers allowed this round`);
      return;
    }
    onSelect(num);
  };

  const visibleVipPatterns = VIP_PATTERNS.filter((p) => !blocked.has(p.value));

  const keypadDisabled = useMemo(() => {
    const disabled = [];
    if (selectedNumbers.length >= maxPicks) {
      for (let d = 0; d <= 9; d += 1) disabled.push(String(d));
      return disabled;
    }
    if (inputBuffer.length === maxDigits - 1) {
      const { min, max } = game.numberRange;
      for (let d = 0; d <= 9; d += 1) {
        const num = parseInt(inputBuffer + String(d), 10);
        if (num >= min && num <= max && blocked.has(num)) disabled.push(String(d));
      }
    }
    return disabled;
  }, [inputBuffer, selectedNumbers.length, game, blocked, maxPicks]);

  return (
    <div className="space-y-4">
      {/* Display */}
      <div className="flex items-center justify-center">
        <div className="w-full max-w-xs">
          <div className="dark-input text-center text-2xl font-mono font-bold tracking-widest py-4 min-h-[60px] flex items-center justify-center">
            {inputBuffer.padEnd(maxDigits, '_') || '____'}
          </div>
        </div>
      </div>

      {/* VIP Quick Picks */}
      {visibleVipPatterns.length > 0 && (
        <div>
          <div className="text-gray-500 text-xs mb-2 text-center">VIP Numbers</div>
          <div className="flex flex-wrap gap-2 justify-center max-w-md mx-auto">
            {visibleVipPatterns.map((pattern) => (
              <button
                key={pattern.label}
                onClick={() => addVipNumber(pattern.value)}
                className="px-3 py-1.5 rounded-lg bg-hash-gold/10 border border-hash-gold/20 text-hash-gold text-xs font-semibold hover:bg-hash-gold/20 transition-colors"
              >
                {pattern.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Keypad */}
      <div className="max-w-xs mx-auto">
        <GameKeypad
          onDigit={handleDigit}
          onBackspace={handleBackspace}
          onClear={handleClear}
          disabledButtons={keypadDisabled}
        />
      </div>
    </div>
  );
}

export default NumberPickerApex;
